<template>
  <div class="role-page">
    <el-card shadow="never" class="card">
      <template #header>
        <div class="card-head">
          <span class="card-title">🔐 权限管理</span>
          <span class="card-hint">
            一个「权限等级」决定两件事：<b>能进后台的哪些页面</b>、<b>有没有删除权限</b>。
            建好之后在「添加用户」里选这个等级就行。
          </span>
          <el-button type="primary" size="small" style="margin-left:auto" @click="openAdd">＋ 新建权限等级</el-button>
        </div>
      </template>

      <el-table :data="roles" v-loading="loading" stripe style="width:100%">
        <el-table-column prop="label" label="等级名称" min-width="130">
          <template #default="s">
            <b>{{ s.row.label }}</b>
            <el-tag v-if="s.row.isSuper" size="small" type="danger" style="margin-left:6px">超管</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="等级标识" min-width="110" />
        <el-table-column label="可访问页面" min-width="120">
          <template #default="s">
            <el-tag size="small" type="info">{{ s.row.isSuper ? '全部' : s.row.pages.length + ' 个' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="删除权限" width="100">
          <template #default="s">
            <el-tag size="small" :type="s.row.canDelete ? 'success' : 'info'">{{ s.row.canDelete ? '有' : '无' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="管权限等级" width="110">
          <template #default="s">
            <el-tag size="small" :type="s.row.canManageRole ? 'success' : 'info'">{{ s.row.canManageRole ? '能' : '不能' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="userCount" label="使用人数" width="90" />
        <el-table-column prop="remark" label="备注" min-width="160" show-overflow-tooltip />
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="s">
            <el-button size="small" :disabled="!!s.row.isSuper" @click="openEdit(s.row)">编辑</el-button>
            <el-button size="small" type="danger" plain :disabled="!!s.row.isSuper || s.row.userCount > 0" @click="doRemove(s.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <p class="tip">
        · <b>超级管理员</b>是硬编码的全权等级，改不了也删不掉（防止把自己锁在门外）<br />
        · 还有用户在用的等级不能删，先把那些用户改成别的等级<br />
        · 没勾「权限管理」的等级，登录后连这一页都看不到
      </p>
    </el-card>

    <!-- 新建 / 编辑 -->
    <el-dialog v-model="dlg" :title="form.id ? ('编辑权限等级：' + form.label) : '新建权限等级'" width="620px" top="6vh">
      <el-form :model="form" label-width="110px">
        <el-form-item label="等级标识">
          <el-input v-model="form.name" :disabled="!!form.id" placeholder="英文标识，例如 editor、ops_1" maxlength="32" style="max-width:240px" />
          <span class="tip">存进数据库用，建好之后不能改（用户在用它）</span>
        </el-form-item>
        <el-form-item label="等级名称">
          <el-input v-model="form.label" placeholder="给人看的名字，例如 运营、客服" maxlength="32" style="max-width:240px" />
        </el-form-item>
        <el-form-item label="删除权限">
          <el-switch v-model="form.canDelete" :active-value="1" :inactive-value="0" />
          <span class="tip">关掉之后这个等级的账号点不了任何删除（服务端也会拦）</span>
        </el-form-item>
        <el-form-item label="管权限等级">
          <el-switch v-model="form.canManageRole" :active-value="1" :inactive-value="0" />
          <span class="tip">能不能进这一页、改别人的权限等级</span>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" placeholder="随便写点，方便以后认" maxlength="60" />
        </el-form-item>

        <el-form-item label="可访问页面">
          <div class="pick-all">
            <el-button size="small" @click="pickAll">全选</el-button>
            <el-button size="small" @click="pickNone">全不选</el-button>
            <span class="tip">已勾选 {{ form.pages.length }} / {{ allPages.length }} 个</span>
          </div>
        </el-form-item>
        <div class="page-groups">
          <div v-for="g in catalog" :key="g.group || 'base'" class="page-group">
            <div class="page-group-title">{{ g.group || '常用' }}</div>
            <el-checkbox-group v-model="form.pages">
              <el-checkbox v-for="it in g.items" :key="it.key" :label="it.key" class="page-check">{{ it.label }}</el-checkbox>
            </el-checkbox-group>
          </div>
        </div>
      </el-form>
      <template #footer>
        <el-button @click="dlg = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="doSave">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import axios from 'axios';

const loading = ref(false);
const saving = ref(false);
const dlg = ref(false);
const roles = ref([]);
const catalog = ref([]);
const allPages = ref([]);

const form = reactive({ id: 0, name: '', label: '', pages: [], canDelete: 0, canManageRole: 0, remark: '' });

const load = async () => {
  loading.value = true;
  try {
    const res = await axios.get('/adminapi/role/list');
    const d = res.data || {};
    if (d.code !== 200) { ElMessage.error(d.message || '读取失败'); return; }
    roles.value = d.roles || [];
    catalog.value = d.catalog || [];
    allPages.value = d.allPages || [];
  } catch (e) {
    ElMessage.error('读取失败：' + (e.response?.data?.message || e.message));
  } finally {
    loading.value = false;
  }
};

const openAdd = () => {
  Object.assign(form, { id: 0, name: '', label: '', pages: [], canDelete: 0, canManageRole: 0, remark: '' });
  dlg.value = true;
};

const openEdit = (row) => {
  Object.assign(form, {
    id: row.id, name: row.name, label: row.label,
    pages: (row.pages || []).slice(), canDelete: row.canDelete, canManageRole: row.canManageRole, remark: row.remark || ''
  });
  dlg.value = true;
};

const pickAll = () => { form.pages = allPages.value.slice(); };
const pickNone = () => { form.pages = []; };

const doSave = async () => {
  if (!form.id && !/^[A-Za-z0-9_\-]{1,32}$/.test(form.name)) {
    ElMessage.warning('等级标识只能用字母数字下划线短横，1~32 位');
    return;
  }
  saving.value = true;
  try {
    const url = form.id ? '/adminapi/role/update' : '/adminapi/role/add';
    const res = await axios.post(url, {
      id: form.id, name: form.name, label: form.label,
      pages: form.pages, canDelete: form.canDelete, canManageRole: form.canManageRole, remark: form.remark
    });
    const d = res.data || {};
    if (d.code === 200) { ElMessage.success(d.message || '已保存'); dlg.value = false; await load(); }
    else ElMessage.error(d.message || '保存失败');
  } catch (e) {
    ElMessage.error('保存失败：' + (e.response?.data?.message || e.message));
  } finally {
    saving.value = false;
  }
};

const doRemove = async (row) => {
  try {
    await ElMessageBox.confirm('确定删除权限等级「' + row.label + '」吗？', '提示', { type: 'warning' });
  } catch (e) { return; }
  try {
    const res = await axios.post('/adminapi/role/remove', { id: row.id });
    const d = res.data || {};
    if (d.code === 200) { ElMessage.success(d.message || '已删除'); await load(); }
    else ElMessage.error(d.message || '删除失败');
  } catch (e) {
    ElMessage.error('删除失败：' + (e.response?.data?.message || e.message));
  }
};

onMounted(load);
</script>

<style scoped>
.role-page { padding: 4px; }
.card-head { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.card-title { font-size: 15px; font-weight: 600; }
.card-hint, .tip { font-size: 12px; color: #909399; line-height: 1.8; }
.pick-all { display: flex; align-items: center; gap: 10px; }
.page-groups { max-height: 320px; overflow: auto; padding: 4px 0 8px 12px; border-left: 2px solid #ebeef5; margin-left: 12px; }
.page-group { margin-bottom: 10px; }
.page-group-title { font-size: 12px; color: #909399; margin-bottom: 2px; }
.page-check { width: 190px; margin-right: 8px; }
</style>
